<div class="flex flex-col h-full">
  
  <!-- Header Actions -->
  <div class="flex items-center justify-between mb-6">
    <div>
      <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
        Logi KSeF
      </h2>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Lista wszystkich logów KSeF
      </p>
    </div>
    <button 
      mat-flat-button 
      color="primary"
      (click)="refresh()"
      [disabled]="isLoading">
      <mat-icon [svgIcon]="'tabler_outline:refresh'"></mat-icon>
      Odśwież
    </button>
  </div>

  <!-- Filters -->
  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      
      <!-- Search -->
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Szukaj</mat-label>
        <mat-icon matPrefix [svgIcon]="'tabler_outline:search'" class="mr-2"></mat-icon>
        <input 
          matInput 
          [(ngModel)]="searchQuery" 
          (ngModelChange)="onSearchChange()"
          placeholder="Email użytkownika, numer faktury lub treść błędu...">
        <button 
          *ngIf="searchQuery" 
          matSuffix 
          mat-icon-button 
          (click)="searchQuery = ''; onSearchChange()">
          <mat-icon [svgIcon]="'tabler_outline:x'"></mat-icon>
        </button>
      </mat-form-field>

      <!-- Log Type Filter -->
      <mat-form-field appearance="outline" class="w-full">
        <mat-label>Typ logu</mat-label>
        <mat-select 
          [(value)]="selectedLogTypes" 
          (selectionChange)="onLogTypesChange($event.value)"
          multiple>
          <mat-option value="all">
            Wszystkie typy
          </mat-option>
          <mat-option *ngFor="let type of availableLogTypes" [value]="type.value">
            {{ type.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
  </div>

  <!-- Stats Bar -->
  <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
    <div class="flex items-center gap-3">
      <mat-icon 
        class="text-red-600 dark:text-red-400"
        [svgIcon]="'tabler_outline:alert-circle'">
      </mat-icon>
      <div>
        <div class="text-sm font-semibold text-red-900 dark:text-red-200">
          Wykryto błędy: {{ totalErrors }}
        </div>
        <div class="text-xs text-red-800 dark:text-red-300 mt-0.5">
          Faktury wymagające uwagi
        </div>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-12">
    <mat-spinner [diameter]="60"></mat-spinner>
    <p class="text-gray-600 dark:text-gray-400 mt-4">Ładowanie błędów...</p>
  </div>

  <!-- Table (desktop) -->
  <div *ngIf="!isLoading" class="flex-1 overflow-hidden bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
    
    <!-- Desktop table - hidden on small screens -->
    <div class="overflow-auto h-full hidden lg:block">
      <table mat-table [dataSource]="dataSource" matSort class="w-full">
        
        <!-- Timestamp Column -->
        <ng-container matColumnDef="timestamp">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="w-40">Data</th>
          <td mat-cell *matCellDef="let error">
            <div class="text-sm">{{ error.timestamp | date:'dd.MM.yyyy' }}</div>
            <div class="text-xs text-gray-500">{{ error.timestamp | date:'HH:mm:ss' }}</div>
          </td>
        </ng-container>

        <!-- Log Type Column -->
        <ng-container matColumnDef="logType">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="w-36">Typ</th>
          <td mat-cell *matCellDef="let error">
            <mat-chip class="text-xs">
              {{ getLogTypeLabelPublic(error.logType) }}
            </mat-chip>
          </td>
        </ng-container>

        <!-- Document Number Column -->
        <ng-container matColumnDef="documentNumber">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Faktura</th>
          <td mat-cell *matCellDef="let error">
            <div class="font-medium text-sm">{{ error.documentNumber }}</div>
          </td>
        </ng-container>

        <!-- User Email Column -->
        <ng-container matColumnDef="userEmail">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Użytkownik</th>
          <td mat-cell *matCellDef="let error">
            <div 
              class="text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-primary hover:underline"
              (click)="searchByEmail(error.userEmail); $event.stopPropagation()"
              [matTooltip]="'Filtruj po tym użytkowniku'">
              {{ error.userEmail }}
            </div>
          </td>
        </ng-container>

        <!-- Error Code Column -->
        <ng-container matColumnDef="errorCode">
          <th mat-header-cell *matHeaderCellDef mat-sort-header class="w-24">Kod</th>
          <td mat-cell *matCellDef="let error">
            <mat-chip [color]="getErrorCodeColor(error.errorCode)">
              {{ error.errorCode }}
            </mat-chip>
          </td>
        </ng-container>

        <!-- Error Message Column -->
        <ng-container matColumnDef="errorMessage">
          <th mat-header-cell *matHeaderCellDef>Błąd</th>
          <td mat-cell *matCellDef="let error">
            <div class="text-sm text-gray-700 dark:text-gray-300 max-w-md truncate">
              {{ error.errorMessage }}
            </div>
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="w-28">Akcje</th>
          <td mat-cell *matCellDef="let error">
            <div class="flex gap-1">
              <button 
                mat-icon-button 
                (click)="openErrorDetails(error)"
                [matTooltip]="'Szczegóły błędu'"
                color="warn">
                <mat-icon [svgIcon]="'tabler_outline:alert-circle'"></mat-icon>
              </button>
              <button 
                *ngIf="error.xml || error.documentId"
                mat-icon-button 
                (click)="openXmlViewer(error)"
                [matTooltip]="'Zobacz XML'"
                color="primary">
                <mat-icon [svgIcon]="'tabler_outline:file-code'"></mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr 
          mat-row 
          *matRowDef="let row; columns: displayedColumns;"
          class="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
        </tr>

        <!-- Empty State (desktop) -->
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell text-center py-12" [attr.colspan]="displayedColumns.length">
            <div class="flex flex-col items-center">
              <mat-icon 
                class="text-gray-400 dark:text-gray-600 mb-4"
                [svgIcon]="'tabler_outline:check-circle'"
                style="width: 64px; height: 64px; font-size: 64px;">
              </mat-icon>
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Brak błędów!
              </h3>
              <p class="text-gray-600 dark:text-gray-400">
                Wszystkie faktury zostały pomyślnie wysłane do KSeF
              </p>
            </div>
          </td>
        </tr>
      </table>
    </div>

    <!-- Mobile/Tablet cards - visible only on small screens -->
    <div class="lg:hidden overflow-auto h-full">

      <!-- Empty state -->
      <div *ngIf="dataSource.data.length === 0" class="flex flex-col items-center py-12 px-4">
        <mat-icon 
          class="text-gray-400 dark:text-gray-600 mb-4"
          [svgIcon]="'tabler_outline:check-circle'"
          style="width: 64px; height: 64px; font-size: 64px;">
        </mat-icon>
        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Brak błędów!</h3>
        <p class="text-gray-600 dark:text-gray-400 text-center">Wszystkie faktury zostały pomyślnie wysłane do KSeF</p>
      </div>

      <!-- Card list -->
      <div class="divide-y divide-gray-100 dark:divide-gray-700">
        <div *ngFor="let error of dataSource.data"
             class="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
          
          <!-- Row 1: Type chip + date -->
          <div class="flex items-center justify-between gap-2 mb-2">
            <mat-chip class="text-xs">{{ getLogTypeLabelPublic(error.logType) }}</mat-chip>
            <span class="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
              {{ error.timestamp | date:'dd.MM.yyyy, HH:mm:ss' }}
            </span>
          </div>

          <!-- Row 2: Document + error code -->
          <div class="flex items-center gap-2 mb-1">
            <span class="font-medium text-sm text-gray-900 dark:text-white truncate">{{ error.documentNumber }}</span>
            <mat-chip *ngIf="error.errorCode !== 'N/A'" [color]="getErrorCodeColor(error.errorCode)" class="text-xs flex-shrink-0">
              {{ error.errorCode }}
            </mat-chip>
          </div>

          <!-- Row 3: User -->
          <div class="text-xs text-gray-600 dark:text-gray-400 mb-1">
            <span class="cursor-pointer hover:text-primary hover:underline" 
                  (click)="searchByEmail(error.userEmail || '')">{{ error.userEmail }}</span>
          </div>

          <!-- Row 4: Error message -->
          <p *ngIf="error.errorMessage" class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2">
            {{ error.errorMessage }}
          </p>

          <!-- Row 5: Actions -->
          <div class="flex gap-2 mt-2">
            <button mat-stroked-button class="!text-xs !h-8" color="warn" (click)="openErrorDetails(error)">
              <mat-icon class="icon-size-4 mr-1" [svgIcon]="'tabler_outline:alert-circle'"></mat-icon>
              Szczegóły
            </button>
            <button *ngIf="error.xml || error.documentId" mat-stroked-button class="!text-xs !h-8" color="primary" (click)="openXmlViewer(error)">
              <mat-icon class="icon-size-4 mr-1" [svgIcon]="'tabler_outline:file-code'"></mat-icon>
              XML
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginator (server-side) -->
    <mat-paginator 
      [length]="totalErrors"
      [pageIndex]="pageIndex"
      [pageSize]="pageSize"
      [pageSizeOptions]="[25, 50, 100]"
      (page)="onPageChange($event)"
      showFirstLastButtons
      class="border-t border-gray-200 dark:border-gray-700">
    </mat-paginator>
  </div>
</div>

